import { Box, Text, Flex } from "@chakra-ui/react"
import type React from "react"
import { memo, useEffect, useRef } from "react"
import { perfDecisionEvent, usePerfRenderCount } from "../perf/perfTrace"
import { SIDEWAYS_MQ } from "./mediaQueries"

// Colour of the "+N" flash: Chakra green.500 / red.500
const FLASH_COLOR = { up: "#38a169", down: "#e53e3e" }

/**
 * One meter: label, bar, value and the per-turn "+N" feedback.
 *
 * The feedback is driven by `nonce` (the turn number of the last choice), not
 * by timers: a new nonce remounts the flash and the pulse wrapper, which
 * restarts their CSS animations (src/index.css, transform/opacity only, so
 * they run on the compositor). Identical consecutive effects still animate
 * because the key changes, and there is no state to get stuck.
 */
const MeterBar: React.FC<{
  label: string
  value: number
  max: number
  icon: React.ReactNode
  /** The last choice's effect on this meter (0 = none) */
  effect: number
  /** Turn number of the last choice; changes once per choice */
  nonce: number
}> = ({ value, max, icon, effect, nonce, label }) => {
  usePerfRenderCount("MeterBar")
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100)
  const minValue = Math.min(Math.max(value, 0), max)

  // Color reflects current value's health: high=green, mid=yellow, low=red
  const ratio = max > 0 ? minValue / max : 0
  const barColor =
    ratio >= 0.66 ? "#22c55e" : ratio >= 0.33 ? "#eab308" : "#ef4444"

  // A persisted lastTurn must not replay its flash on reload: only turns
  // taken after this meter mounted animate.
  const mountNonce = useRef(nonce)
  const showFlash = effect !== 0 && nonce !== mountNonce.current

  useEffect(() => {
    if (showFlash) perfDecisionEvent("flash")
  }, [nonce, showFlash])

  return (
    // Label over the bar over the value, except on a phone held sideways,
    // where the three go on one line: icon and label, bar, value. The track
    // height for that line is in src/index.css.
    <Box
      width="100%"
      position="relative"
      overflow="visible"
      sx={{
        [SIDEWAYS_MQ]: {
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr) 26px",
          alignItems: "center",
          columnGap: "6px",
        },
      }}
    >
      {showFlash && (
        <span
          key={`flash-${nonce}`}
          className="meter-flash"
          style={{ color: effect > 0 ? FLASH_COLOR.up : FLASH_COLOR.down }}
          aria-live="polite"
        >
          {effect > 0 ? "+" : ""}
          {effect}
        </span>
      )}

      {/* Persistent label with icon */}
      <Flex
        alignItems="center"
        justifyContent="center"
        mb={2}
        gap={2}
        sx={{
          [SIDEWAYS_MQ]: {
            marginBottom: 0,
            justifyContent: "flex-start",
            gap: "4px",
            "& svg": { width: "16px", height: "16px" },
          },
        }}
      >
        {icon}
        <Text
          fontWeight="bold"
          fontSize={{ base: "sm", md: "md" }}
          sx={{ [SIDEWAYS_MQ]: { fontSize: "14px" } }}
        >
          {label}
        </Text>
      </Flex>

      {/* Bar: plain elements, pulsed via a keyed CSS animation */}
      <div
        key={showFlash ? `pulse-${nonce}` : "bar"}
        className={showFlash ? "meter-pulse" : undefined}
      >
        <div
          className="meter-track"
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-valuenow={minValue}
        >
          <div
            className="meter-fill"
            style={{ width: `${percentage}%`, backgroundColor: barColor }}
          />
        </div>
      </div>

      {/* Score display */}
      <Text
        textAlign="center"
        mt={2}
        fontSize={{ base: "md", md: "lg" }}
        fontWeight="bold"
        sx={{
          [SIDEWAYS_MQ]: { marginTop: 0, fontSize: "14px", textAlign: "right" },
        }}
      >
        {minValue}
      </Text>
    </Box>
  )
}

export default memo(MeterBar)
